import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { BiSearchAlt, BiX } from 'react-icons/bi';
import { FaFilm } from 'react-icons/fa';
import tmdbService from '../services/tmdb';
import Poster from '../components/poster/Poster.component';
import { PosterSliderSkeleton } from '../components/common/LoadingSkeleton';

const SearchResultsPage = () => {
  const [params] = useSearchParams();
  const query = (params.get('q') || '').trim();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const runSearch = async () => {
      setLoading(true);
      setResults([]);
      window.scrollTo(0, 0);
      if (!query) {
        setLoading(false);
        if (isMounted) setResults([]);
        return;
      }
      try {
        const [movieHits, tvHits] = await Promise.all([
          tmdbService.searchMovies(query),
          tmdbService.searchTV(query),
        ]);
        const combined = [
          ...(movieHits || []).map((m) => ({ ...m, media_type: 'movie' })),
          ...(tvHits || []).map((t) => ({ ...t, media_type: 'tv' })),
        ];
        const unique = combined.filter(
          (item, index, self) => index === self.findIndex((x) => x.id === item.id && x.media_type === item.media_type)
        );
        if (isMounted) setResults(unique);
      } catch (err) {
        console.error('Search failed:', err);
        if (isMounted) setResults([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    runSearch();
    return () => {
      isMounted = false;
    };
  }, [query]);

  return (
    <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      {/* Header Banner */}
      <div className="mb-8 bg-gradient-to-r from-dark-800 via-dark-800 to-dark-700 border border-white/10 rounded-3xl p-6 sm:p-10 shadow-2xl relative overflow-hidden">
        <div className="max-w-2xl relative z-10">
          <span className="bg-accent-gold text-dark-900 font-extrabold text-xs px-3 py-1 rounded-full uppercase tracking-wider mb-3 inline-block">
            <BiSearchAlt className="inline-block w-3.5 h-3.5 mr-1" />
            Search Results
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight mb-2 tracking-tight flex items-center gap-3 flex-wrap">
            Results for <span className="text-accent-gold">"{query}"</span>
          </h1>
          <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">
            Movies and TV series matched from TMDB for your search term.
          </p>
        </div>
      </div>

      {/* Results Grid */}
      {loading ? (
        <PosterSliderSkeleton count={12} />
      ) : results.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 sm:gap-6 animate-fade-in">
          {results.map((item) => (
            <Poster {...item} key={`${item.media_type}-${item.id}`} />
          ))}
        </div>
      ) : (
        <div className="text-center py-20">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-dark-800 border border-white/10 flex items-center justify-center">
            <FaFilm className="w-7 h-7 text-accent-gold" />
          </div>
          <h3 className="text-white font-bold text-lg mb-1">No results found</h3>
          <p className="text-gray-400 text-sm max-w-sm mx-auto mb-6">
            We couldn't find anything matching "{query}". Try a different spelling or a shorter name.
          </p>
          <Link
            to="/movies"
            className="inline-flex items-center gap-2 bg-accent-gold hover:bg-accent-goldHover text-dark-900 font-bold text-sm px-5 py-2.5 rounded-xl transition-all shadow-md"
          >
            <BiX className="w-4 h-4" />
            Browse All Movies
          </Link>
        </div>
      )}
    </div>
  );
};

export default SearchResultsPage;